import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  BiContextProvidedValue,
  BiData,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import GroupIcon from '@wix/da-ds/pkg/Icons/24x24/Group';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { useIsCurrentUserDeviationAuthor } from '../../utils/hooks/useIsCurrentUserDeviationAuthor';
import useDeviationStats from '../useDeviationStats';
import Stat, { Props as StatProps } from '../Stat';

export interface Props extends Omit<StatProps, 'statNameLocaleKey'> {
  deviation: PapiDeviation;
  className?: string;
  /** don't display language after stat count */
  onlyNumbers?: boolean;
}

const GroupStat: React.FC<Props> = ({
  deviation,
  onlyNumbers,
  className,
  ...baseProps
}) => {
  const { t } = useTranslation();
  // note: won't be accurate if extended isn't loaded
  const { groups } = useDeviationStats(deviation);
  const currentUserDeviationAuthor = useIsCurrentUserDeviationAuthor(deviation);
  const group_stats = useFlag('group_stats');
  const hideStat =
    !group_stats || (groups === 0 && !currentUserDeviationAuthor);

  return hideStat ? null : (
    <Stat
      className={className}
      deviation={deviation}
      statCount={groups}
      icon={GroupIcon}
      onlyNumbers={onlyNumbers}
      statNameLocaleKey="deviation.stats.groups"
      title={t('deviation.stats.tooltip.groups')}
      biData={BiData<DefaultBiEvent>({
        evid: 2,
        click_info: 'groupsStats',
        sectionname: BiContextProvidedValue,
        widgetname: BiContextProvidedValue,
        typeid: BiContextProvidedValue,
        itemid: BiContextProvidedValue,
      })}
      {...baseProps}
    />
  );
};
GroupStat.displayName = 'GroupStat';

export default GroupStat;
